import { Component, HostListener, inject, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CompositeArticleDetailTableModel } from 'src/app/models/tables/preliminaries/composite-article-detail-table.model';
import { CompositeArticleTableModel } from 'src/app/models/tables/preliminaries/composite-article-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CompositeArticleDetailService } from 'src/app/services/preliminaries/composite-article-detail.service';
import { CompositeArticleService } from 'src/app/services/preliminaries/composite-article.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { ImportCompositeArticleComponent } from '../import-composite-article/import-composite-article.component';

@Component({
  selector: 'app-composite-article',
  templateUrl: './composite-article.component.html',
  styleUrl: './composite-article.component.scss',
  standalone: false
})
export class CompositeArticleComponent {

  private readonly compositeArticleService = inject(CompositeArticleService);
  private readonly compositeArticleDetailService = inject(CompositeArticleDetailService);
  private readonly headerService = inject(HeaderService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  @ViewChild('importCompositeArticleModal') importCompositeArticleModalComponent?: CustomModalComponent | null = null;

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  componentImportCompositeArticleModal = ImportCompositeArticleComponent;
  detailData: PagedResponseModel<CompositeArticleDetailTableModel> =
    new PagedResponseModel<CompositeArticleDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = []
  generalData: PagedResponseModel<CompositeArticleTableModel> =
    new PagedResponseModel<CompositeArticleTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined
  limit: number = 10;
  nameWindow: string = 'Artículos Compuestos';
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  url: string = 'composite-article';
  permissionUpdatable: boolean = false;

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getCompositeArticlesWithPagination()
    ]);
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(
        this.compositeArticleDetailService.exportDetailConsultation(
          this.filters, this.columnSort, this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.nameWindow} Detalle.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(`Error al exportar los detalles de los ${this.nameWindow}.`, error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(
        this.compositeArticleService.exportGeneralConsultation(
          this.filters, this.columnSort, this.ascendant
        )
      );
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.nameWindow}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(`Error al exportar los detalles de los ${this.nameWindow}.`, error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(
          this.compositeArticleDetailService.getColumnsForDetailConsultation()
        );
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta de detalles.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        const response = await firstValueFrom(
          this.compositeArticleService.getColumnsForGeneralConsultation()
        );
        this.generalHeader = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta general.', error);
    }
  }

  async getCompositeArticlesWithPagination() {
    try {
      const response = await firstValueFrom(
        this.compositeArticleService.getAllCompositeArticlesWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
      this.generalData = response;
    } catch (error) {
      console.error('Error al obtener los artículos compuestos.', error);
    }
  }

  async getCompositeArticleDetailsWithPagination() {
    try {
      const response = await firstValueFrom(
        this.compositeArticleDetailService.getCompositeArticleDetailsWithPagination(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
      this.detailData = response;
    } catch (error) {
      console.error(`Error al obtener los detalles de los ${this.nameWindow}.`, error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionProfileByUrl();
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(
        this.windowService.getPermissionProfileByUrl(this.url)
      );
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    if (this.activeTabIndex === tabIndex) return;
    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;
    switch (this.activeTabIndex) {
      case 0: {
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getCompositeArticlesWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      }
      case 1: {
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getCompositeArticleDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      }
    }
  }

  newRegister() {
    this.router.navigate([`/${this.url}/register`]);
  }

  onAction(event: { action: string, item?: CompositeArticleTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.id}`]);
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0: {
        await this.exportGeneralConsultation();
        break;
      }
      case 1: {
        await this.exportDetailConsultation();
        break;
      }
    }
  }

  async onImport() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para la edición de ${this.nameWindow}. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.importCompositeArticleModalComponent?.show();
  }

  async reloadList(reload: any) {
    if (reload) {
      await this.getCompositeArticlesWithPagination();
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0: {
        await this.getCompositeArticlesWithPagination()
        break;
      }
      case 1: {
        await this.getCompositeArticleDetailsWithPagination()
        break;
      }
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
